import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";
import {
  assetQueryOptions,
  clearAssetComment,
  deallocateAsset,
  lastCompletedAllocationResultTypeSchema,
  lastCompletedAssetAllocationQueryOptions,
  updateAssetComment,
} from "@/api/Equipment";
import {
  getAssetStatusLabel,
  type equipmentAssetStatusSchema,
} from "@medoma/models/equipment";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusDictionary,
  patientStatusSchema,
} from "@medoma/models/patients";
import { employeeName } from "@medoma/models/shifts";
import { humanFormat } from "@medoma/models/date-and-time";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import TextArea from "@/components/TextArea/TextArea";
import Form from "@/components/Form/Form";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { formattedDate } from "@/components/Time/timeUtils";
import styles from "./AssetDetails.module.scss";
import { z } from "zod";

type Asset = Awaited<
  ReturnType<ReturnType<typeof assetQueryOptions>["queryFn"]>
>;
type AllocatedAsset = Extract<
  Asset,
  { status: typeof equipmentAssetStatusSchema.enum.allocated }
>;

const AssignedPatient = ({ asset }: { asset: AllocatedAsset }) => {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const queryClient = useQueryClient();

  const patientName =
    asset.patient.status === patientStatusSchema.enum.deleted
      ? t(patientStatusDictionary.deleted.singular)
      : getPatientFullName(asset.patient.name);

  const { mutate: returnAsset, isPending: isReturningAsset } = useMutation({
    mutationFn: deallocateAsset,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att återlämna utrustningen.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
    },
  });

  const handleReturn = async () => {
    const assetName = asset.name;
    const confirmed = await confirm({
      title: t`Bekräfta återlämning`,
      message: t`Är du säker på att ${assetName} har återlämnats?`,
    });
    if (!confirmed) {
      return;
    }
    returnAsset({ assetId: asset.id });
  };

  return (
    <div className={styles.allocationBox}>
      <div className={styles.allocationDetails}>
        <Text element="span" weight="medium">
          {patientName}
        </Text>
        {asset.patient.status !== patientStatusSchema.enum.deleted ? (
          <Text element="span" color="faded">
            {formatPersonalIdentityNumber({
              personalIdentityNumber: asset.patient.personalIdentityNumber,
              personalIdentityNumberType:
                asset.patient.personalIdentityNumberType,
            })}
          </Text>
        ) : null}
        <Text element="span" color="faded">
          {humanFormat(asset.allocatedAt, { dateStyle: "short" })}
        </Text>
      </div>
      <FilledButton
        size="small"
        onClick={handleReturn}
        disabled={isReturningAsset}
      >
        <Trans>Återlämna</Trans>
      </FilledButton>
    </div>
  );
};

const LastCompletedAllocation = ({ assetId }: { assetId: string }) => {
  const { t } = useLingui();
  const { data: lastCompletedAllocation } = useQuery(
    lastCompletedAssetAllocationQueryOptions({ assetId }),
  );
  if (
    !lastCompletedAllocation ||
    lastCompletedAllocation.resultType ===
      lastCompletedAllocationResultTypeSchema.enum.none
  ) {
    return null;
  }

  if (
    lastCompletedAllocation.resultType ===
    lastCompletedAllocationResultTypeSchema.enum.unmatched
  ) {
    return (
      <section className={styles.allocationSection}>
        <Heading level="h2" size="large" margin="bottom">
          <Trans>Senaste tilldelning</Trans>
        </Heading>
        <ErrorMessage
          message={t`Historiken kan inte visas. Kontakta support.`}
        />
      </section>
    );
  }

  const { patient, allocatedAt, deallocatedAt, returnedByEmployee } =
    lastCompletedAllocation;

  const patientName =
    patient.status === patientStatusSchema.enum.deleted
      ? t(patientStatusDictionary.deleted.singular)
      : getPatientFullName(patient.name);

  const returnedBy = employeeName(returnedByEmployee, {
    employeeNameFormat: "full",
  });

  return (
    <section className={styles.allocationSection}>
      <Heading level="h2" size="large" margin="bottom">
        <Trans>Senaste tilldelning</Trans>
      </Heading>
      <div className={styles.allocationBox}>
        <div className={styles.allocationDetails}>
          <Text element="span" weight="medium">
            {patientName}
          </Text>
          {patient.status !== patientStatusSchema.enum.deleted ? (
            <Text element="span" color="faded">
              {formatPersonalIdentityNumber({
                personalIdentityNumber: patient.personalIdentityNumber,
                personalIdentityNumberType: patient.personalIdentityNumberType,
              })}
            </Text>
          ) : null}
          <Text element="span" color="faded">
            <Trans>Tilldelad </Trans> {formattedDate(allocatedAt)}
          </Text>
          <Text element="span" color="faded">
            <Trans>Återlämnad </Trans> {formattedDate(deallocatedAt)} av{" "}
            {returnedBy}
          </Text>
        </div>
      </div>
    </section>
  );
};

const CommentEditor = ({ asset }: { asset: Asset }) => {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const comment = asset.comment?.trim() ? asset.comment : null;
  const { register, handleSubmit, reset } = useForm<{ comment: string }>({
    defaultValues: { comment: comment ?? "" },
  });

  const { mutate: saveComment, isPending: isSavingComment } = useMutation({
    mutationFn: updateAssetComment,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att spara kommentaren.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      setIsEditing(false);
    },
  });

  const { mutate: removeComment, isPending: isRemovingComment } = useMutation({
    mutationFn: clearAssetComment,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort kommentaren.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      reset({ comment: "" });
    },
  });

  const handleClear = async () => {
    const confirmed = await confirm({
      title: t`Ta bort kommentar`,
      message: t`Är du säker på att du vill ta bort kommentaren?`,
    });
    if (!confirmed) {
      return;
    }
    removeComment({ assetId: asset.id });
  };

  return (
    <div className={styles.fieldWithAction}>
      <Text element="span">{comment ?? "–"}</Text>
      <div className={styles.actions}>
        {isEditing ? (
          <PlainButton
            size="small"
            onClick={() => {
              reset({ comment: comment ?? "" });
              setIsEditing(false);
            }}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <>
            <PlainButton size="small" onClick={() => setIsEditing(true)}>
              {comment ? <Trans>Ändra</Trans> : <Trans>Lägg till</Trans>}
            </PlainButton>
            {comment ? (
              <PlainButton
                size="small"
                onClick={handleClear}
                disabled={isRemovingComment}
              >
                <Trans>Ta bort</Trans>
              </PlainButton>
            ) : null}
          </>
        )}
      </div>
      {isEditing ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(({ comment: nextComment }) => {
              saveComment({ assetId: asset.id, comment: nextComment });
            })}
          >
            <Form.Row>
              <TextArea
                label={t`Kommentar`}
                showOptionalLabel
                {...register("comment")}
              />
            </Form.Row>
            <FilledButton type="submit" disabled={isSavingComment}>
              <Trans>Spara</Trans>
            </FilledButton>
          </Form>
        </div>
      ) : null}
    </div>
  );
};

export const AssetDetails = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { assetId } = z.object({ assetId: z.string() }).parse(useParams());

  const {
    data: asset,
    isPending: isPendingAsset,
    isError: isErrorAsset,
    error: assetError,
  } = useQuery(assetQueryOptions({ assetId }));

  if (isPendingAsset) {
    return <Loading message={t`Hämtar utrustning`} padding={24} />;
  }

  if (isErrorAsset || !asset) {
    if (isErrorAsset) {
      Sentry.captureException(assetError);
    }
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta utrustningen.`} ${deducedError(assetError)}`}
        padding={24}
      />
    );
  }

  return (
    <article key={asset.id}>
      <header className={styles.header}>
        <Heading level="h1">{asset.name}</Heading>
        <CloseButton close={() => navigate("..")} />
      </header>

      <section>
        <DescriptionList>
          <DescriptionList.Term>
            <Trans>Kategori</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            {asset.category.name}
          </DescriptionList.Description>
          <DescriptionList.Term>
            <Trans>Status</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            {t(getAssetStatusLabel(asset))}
          </DescriptionList.Description>
          <DescriptionList.Term>
            <Trans>Kommentar</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            <CommentEditor asset={asset} />
          </DescriptionList.Description>
        </DescriptionList>
      </section>

      {asset.status === "allocated" ? (
        <section className={styles.allocationSection}>
          <Heading level="h2" size="large" margin="bottom">
            <Trans>Tilldelad patient</Trans>
          </Heading>
          <AssignedPatient asset={asset} />
        </section>
      ) : null}

      <LastCompletedAllocation assetId={asset.id} />
    </article>
  );
};
